import { useCallback, useState } from 'react';

import {
	applyCommitEditVersion,
	applyCommitGenerate,
	applyCommitVersion,
	applyMarkDirty,
	applyRedo,
	applyUndo,
	createVersionStackState,
	deriveVersionLabel,
} from './setlistVersionBranching';
import type {
	SetlistVersionSnapshot,
	VersionStackState,
} from './setlistVersionBranching';

// Session-scoped, in-memory version history for a GenerateSetlist song
// array.
//
// This hook is a thin React wrapper — it holds state only. Every
// transition (commitVersion/markDirty/commitGenerate/undo/redo/
// resetVersions) routes through the pure, React-free reducer in
// `setlistVersionBranching.ts`, which can be executed directly by a Node
// test harness outside React. This module deliberately never persists
// anything itself — no localStorage, sessionStorage, IndexedDB, cookies,
// or network/GraphQL calls. History lives entirely in React state and
// dies with the component (SETVER-11). Lock state (`lockedTeamSongIds`)
// and key overrides (`songOverrides`) are explicitly out of scope per
// SETVER-09 and must never appear in a snapshot.
//
// SETVER-16 through SETVER-20 add an interaction-aware branching model on
// top of the above: Generate with no interaction since the current
// top-level Setlist's last generate appends a brand-new Setlist entry at
// the end of the flattened history (never deleting anything); Generate
// after an interaction that is still "pending" (a slot-count change, or
// locked songs) splices a new Version entry in immediately after the
// pointer (also never deleting anything).
//
// A song switch and a finished reorder session ("Done") are a deliberate,
// finished choice rather than an in-progress step: they call
// `commitEditVersion` instead of `commitVersion`, which finalizes
// immediately as its own new, distinctly-numbered Version (the S#.V#
// label advances right away, no Generate needed). Because it behaves
// like its own mini-Generate, it splices non-destructively after the
// pointer (like `commitGenerate`'s version-branch) instead of truncating
// ahead the way `commitVersion` does — editing under an earlier Setlist
// you undid back into never deletes the Setlists/Versions ahead of it.
// Because that edit is already fully finalized, they do NOT call
// `markDirty()` — pressing Generate right after a switch or a reorder,
// with no further edit, starts a fresh top-level Setlist rather than
// branching yet another Version on top. Only a slot-count change (the
// song-count panel's Save, still queued via `commitVersion` +
// `markDirty()`) or locked songs still make the *next* Generate branch a
// Version instead.
//
// Locked songs are a standing constraint rather than a one-off edit:
// `commitGenerate` also takes a live `hasActiveLocks` flag, so Generate
// keeps branching a Version for as long as at least one song stays
// locked, even across repeated Generates with no other edit in between.
//
// `dirtySetlistNumber: number | null`, tracked inside
// `setlistVersionBranching.ts`'s `VersionStackState`, holds the
// setlistNumber of the Setlist that has been interacted with since its
// own last Generate. It is set by `markDirty()` at the one remaining
// edit-type interaction site (the song-count panel's Save) to the
// pointer entry's `setlistNumber`, and compared against the pointer
// entry's `setlistNumber` inside `commitGenerate` so a dirty mark made
// under one Setlist can never route a Generate made under a different
// one (the WR-02 fix). It is cleared on every Generate and on save, and
// never leaks across this hook's boundary.
// `commitVersion`, `undo`, `redo` and `resetVersions` keep their exact
// Phase 83 truncate/collapse semantics per D-01.

export { areSnapshotsEqual } from './setlistVersionBranching';
export type { SetlistVersionSnapshot } from './setlistVersionBranching';

export const useSetlistVersionStack = (
	initialSnapshot: SetlistVersionSnapshot,
): {
	versionLabel: string;
	canUndo: boolean;
	canRedo: boolean;
	commitVersion: (snapshot: SetlistVersionSnapshot) => void;
	commitEditVersion: (snapshot: SetlistVersionSnapshot) => void;
	commitGenerate: (
		snapshot: SetlistVersionSnapshot,
		hasActiveLocks: boolean,
	) => void;
	markDirty: () => void;
	undo: () => SetlistVersionSnapshot | undefined;
	redo: () => SetlistVersionSnapshot | undefined;
	resetVersions: (snapshot: SetlistVersionSnapshot) => void;
} => {
	const [state, setState] = useState<VersionStackState>(() =>
		createVersionStackState(initialSnapshot),
	);

	const versionLabel = deriveVersionLabel(state);
	const canUndo = state.pointer > 0;
	const canRedo = state.pointer < state.versions.length - 1;

	const commitVersion = useCallback((snapshot: SetlistVersionSnapshot) => {
		setState((prev) => applyCommitVersion(prev, snapshot));
	}, []);

	const commitEditVersion = useCallback(
		(snapshot: SetlistVersionSnapshot) => {
			setState((prev) => applyCommitEditVersion(prev, snapshot));
		},
		[],
	);

	const markDirty = useCallback(() => {
		setState(applyMarkDirty);
	}, []);

	const commitGenerate = useCallback(
		(snapshot: SetlistVersionSnapshot, hasActiveLocks: boolean) => {
			setState((prev) =>
				applyCommitGenerate(prev, snapshot, hasActiveLocks),
			);
		},
		[],
	);

	const undo = useCallback((): SetlistVersionSnapshot | undefined => {
		if (state.pointer <= 0) {
			return undefined;
		}

		const target = state.versions[state.pointer - 1];
		setState(applyUndo);
		return target.snapshot;
	}, [state]);

	const redo = useCallback((): SetlistVersionSnapshot | undefined => {
		if (state.pointer >= state.versions.length - 1) {
			return undefined;
		}

		const target = state.versions[state.pointer + 1];
		setState(applyRedo);
		return target.snapshot;
	}, [state]);

	const resetVersions = useCallback((snapshot: SetlistVersionSnapshot) => {
		setState(createVersionStackState(snapshot));
	}, []);

	return {
		versionLabel,
		canUndo,
		canRedo,
		commitVersion,
		commitEditVersion,
		commitGenerate,
		markDirty,
		undo,
		redo,
		resetVersions,
	};
};
